import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import { blogPosts as mockBlogPosts } from '../data/mock';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        
        // Fetch the specific post
        const response = await fetch(`${API_URL}/api/blog/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPost(data);
          
          // Fetch all posts for related articles
          const allResponse = await fetch(`${API_URL}/api/blog`);
          if (allResponse.ok) {
            const allData = await allResponse.json();
            setAllPosts(allData);
          }
        } else if (response.status === 404) {
          // Try mock data as fallback
          const mockPost = mockBlogPosts.find(p => p.slug === slug);
          if (mockPost) {
            setPost(mockPost);
            setAllPosts(mockBlogPosts);
          } else {
            setNotFound(true);
          }
        } else {
          // Fallback to mock data
          const mockPost = mockBlogPosts.find(p => p.slug === slug);
          if (mockPost) {
            setPost(mockPost);
            setAllPosts(mockBlogPosts);
          } else {
            setNotFound(true);
          }
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
        // Fallback to mock data
        const mockPost = mockBlogPosts.find(p => p.slug === slug);
        if (mockPost) {
          setPost(mockPost);
          setAllPosts(mockBlogPosts);
        } else {
          setNotFound(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-20">
        <div className="container py-12 text-center">
          <p className="body-large">Loading...</p>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="pt-20">
        <div className="container py-12 text-center">
          <h1 className="heading-1 mb-4">Post not found</h1>
          <Link to="/blog" className="btn-primary">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Get related posts
  const relatedPosts = allPosts.filter(p => p.slug !== post.slug && p.category === post.category).slice(0, 2);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--gradient-hero-subtle)', paddingTop: '4rem' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 body-small mb-6 hover:text-brand-orange transition-colors">
              <ArrowLeft size={18} />
              Back to Blog
            </Link>
            
            <div className="flex items-center gap-4 mb-4">
              <span className="caption px-3 py-1 rounded-full" style={{ background: 'var(--accent-blue-200)' }}>
                {post.category}
              </span>
              <div className="flex items-center gap-1 caption">
                <Calendar size={14} />
                {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
              <div className="flex items-center gap-1 caption">
                <User size={14} />
                {post.author}
              </div>
            </div>

            <h1 className="heading-hero mb-6">{post.title}</h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {post.excerpt}
            </p>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="section" style={{ background: 'var(--bg-page)', paddingTop: '2rem' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <img 
              src={post.image}
              alt={post.title}
              className="w-full rounded-lg shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="card">
              <div className="prose max-w-none">
                {post.content ? (
                  <div 
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                    style={{ color: 'var(--text-secondary)' }}
                  />
                ) : (
                  <>
                    <p className="body-large mb-6">
                      This is where the full blog post content would appear. The article would include 
                      detailed information about {post.title.toLowerCase()}, practical examples, and 
                      actionable advice for parents.
                    </p>

                    <h2 className="heading-2 mb-4 mt-8">Understanding the Challenge</h2>
                    <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                      Many parents struggle with knowing exactly what to say during difficult moments. 
                      Having specific scripts and language patterns can make all the difference in how 
                      your child responds and learns.
                    </p>

                    <h2 className="heading-2 mb-4 mt-8">Key Strategies</h2>
                    <ul className="space-y-3 mb-6">
                      <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                        • Start with validation before moving to problem-solving
                      </li>
                      <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                        • Use consistent language patterns so your child learns to recognize them
                      </li>
                      <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                        • Practice during calm moments, not just during conflicts
                      </li>
                      <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                        • Remember that repetition is key—skills develop over time
                      </li>
                    </ul>

                    <h2 className="heading-2 mb-4 mt-8">Putting It Into Practice</h2>
                    <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                      The Emotional Intelligence Academy curriculum provides these exact scripts and 
                      practice opportunities for every age level. Each book includes 40 weeks of 
                      structured learning with daily activities that make emotional skills development 
                      manageable for busy families.
                    </p>
                  </>
                )}

                <div className="p-6 rounded-lg mt-8" style={{ background: 'var(--accent-blue-200)' }}>
                  <h3 className="heading-3 mb-3">Ready to Learn More?</h3>
                  <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                    Explore our age-specific curriculum to find the right starting point for your child.
                  </p>
                  <Link to="/start-here" className="btn-primary">
                    Find Your Child's Book
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="section" style={{ background: 'var(--bg-section)' }}>
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="heading-2 mb-6">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.map(relatedPost => (
                  <Link 
                    key={relatedPost.id || relatedPost.slug}
                    to={`/blog/${relatedPost.slug}`}
                    className="card hover-lift"
                    style={{ padding: 0, overflow: 'hidden' }}
                  >
                    <img 
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-40 object-cover"
                    />
                    <div className="p-4">
                      <span className="caption px-2 py-1 rounded-full" style={{ background: 'var(--accent-orange-200)' }}>
                        {relatedPost.category}
                      </span>
                      <h3 className="heading-3 mt-3 mb-2">{relatedPost.title}</h3>
                      <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetailPage;
